import graphql__91d21cb2d851dc224a2b from "./__generated__/MergeSupplierDialogMutation.graphql.ts";import * as React from "react";
import {graphql} from "react-relay";
import {useRelayEnvironment} from "react-relay/hooks";
import {useIntl} from "react-intl";
import * as yup from "yup";
import FormDialog from "./FormDialog";
import Section from "../form/Section";
import Row from "../form/Row";
import Label from "../form/Label";
import Field from "../form/Field";
import {commitMutationAsPromise} from "../../helper/commitMutationWithErrorHandling";
import {useErrorContext} from "../../contexts/ErrorContext";
import type {MergeSupplierDialogMutation} from "./__generated__/MergeSupplierDialogMutation.graphql";

type Supplier = {
    supplierIdentifier: string
    name: string
    spellings: ReadonlyArray<string>
}

type Props = {
    supplier: Supplier
    otherSuppliers: ReadonlyArray<Supplier>
    onClose: (changed: boolean) => void
}

const assignSupplierSpellingMutation = graphql__91d21cb2d851dc224a2b;

/**
 * Merges a supplier into another one by assigning all its spellings to it. The backend removes
 * the supplier once its last spelling is gone.
 */
const MergeSupplierDialog: React.FC<Props> = ({supplier, otherSuppliers, onClose}) => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();
    const sortedSuppliers = [...otherSuppliers].sort((a, b) => a.name.localeCompare(b.name));

    const assignSpelling = (spelling: string, targetSupplierIdentifier: string) => commitMutationAsPromise<MergeSupplierDialogMutation>(
        relayEnvironment,
        assignSupplierSpellingMutation,
        {input: {id: targetSupplierIdentifier, spelling}},
        addError,
        intl.formatMessage({id: 'errors.failedToMergeSupplier'})
    );

    const handleSubmission = async (values) => {
        try {
            for (const spelling of supplier.spellings) {
                await assignSpelling(spelling, values.targetSupplierIdentifier);
            }
        } catch {
            // The error was already reported, the list shows what was merged so far
        }
        onClose(true);
    };

    const validationSchema = yup.object().shape({
        targetSupplierIdentifier: yup.string().required(intl.formatMessage({id: 'validation.field.required'})),
    });

    return (
        <FormDialog handleCloseDialog={() => onClose(false)}
                    handleSubmission={handleSubmission}
                    initialValues={{targetSupplierIdentifier: ''}}
                    validationSchema={validationSchema}
                    headline={intl.formatMessage({id: 'dialog.MergeSupplierDialog.headline'}, {name: supplier.name})}
                    submitLabel={intl.formatMessage({id: 'dialog.MergeSupplierDialog.submit'})}>
            <Section>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.MergeSupplierDialog.targetLabel'})}</Label>
                    <Field name="targetSupplierIdentifier" as="select" autoFocus={true}>
                        <option value="" disabled>{intl.formatMessage({id: 'dialog.MergeSupplierDialog.targetPlaceholder'})}</option>
                        {sortedSuppliers.map(otherSupplier => (
                            <option key={otherSupplier.supplierIdentifier} value={otherSupplier.supplierIdentifier}>{otherSupplier.name}</option>
                        ))}
                    </Field>
                </Row>
                <p className="text-sm text-gray-500">
                    {intl.formatMessage({id: 'dialog.MergeSupplierDialog.description'}, {name: supplier.name, count: supplier.spellings.length})}
                </p>
            </Section>
        </FormDialog>
    );
};

export default MergeSupplierDialog;
